import { TeammateType } from "@repo/app-types/entities/teammate";
import { IconUser, IconUserBolt } from "@tabler/icons-react";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { Size } from "../helpers/helpers.js";

export interface TeammateLabelProps {
  name: string;
  kind: "human" | "voiceAgent";
  size?: TeammateLabelSize;
  /** Shows the name alone, without the initials or voice-agent icon. */
  isAvatarHidden?: boolean;
}

type TeammateLabelSize = typeof Size.SMALL | typeof Size.MEDIUM;

const labelSizeClassNames: Record<TeammateLabelSize, string> = {
  [Size.SMALL]: "gap-1 text-xs leading-4",
  [Size.MEDIUM]: "gap-1.5 text-xs",
};

const avatarSizeClassNames: Record<TeammateLabelSize, string> = {
  [Size.SMALL]: "size-4 text-[8px] leading-none",
  [Size.MEDIUM]: "size-5 text-2xs",
};

const iconSizeClassNames: Record<TeammateLabelSize, string> = {
  [Size.SMALL]: "size-3",
  [Size.MEDIUM]: "size-3.5",
};

/** The label kind for a teammate's type. */
export function teammateLabelKind(
  type: TeammateType,
): TeammateLabelProps["kind"] {
  return type === TeammateType.AGENT ? "voiceAgent" : "human";
}

/** First letters of the name's first and last words, e.g. "Jane Q. Smith" gives "JS". */
function buildInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words.at(0)?.charAt(0) ?? "";
  const last = words.length > 1 ? (words.at(-1)?.charAt(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

/** A teammate's avatar and name: initials for people, an icon for voice agents. */
export const TeammateLabel: FC<TeammateLabelProps> = (props) => {
  const { name, kind, size = Size.MEDIUM, isAvatarHidden = false } = props;

  return (
    <span
      className={twMerge(
        "inline-flex max-w-full min-w-0 items-center",
        labelSizeClassNames[size],
      )}
    >
      {!isAvatarHidden && (
        <span
          aria-hidden="true"
          className={twMerge(
            "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md font-medium",
            avatarSizeClassNames[size],
            kind === "voiceAgent"
              ? "bg-violet-50 text-violet-700"
              : "bg-blue-50 text-blue-700",
          )}
        >
          {kind === "voiceAgent" ? (
            <IconUserBolt className={iconSizeClassNames[size]} />
          ) : (
            buildInitials(name) || (
              <IconUser className={iconSizeClassNames[size]} />
            )
          )}
        </span>
      )}
      <span className="min-w-0 truncate font-medium text-foreground">
        {name}
      </span>
      {kind === "voiceAgent" && <span className="sr-only">(voice agent)</span>}
    </span>
  );
};
